/**
 * weighBarcode.ts — Codificación/decodificación de etiquetas de pesaje ALTENOT.
 *
 * QUÉ HACE:
 *  - Define el formato del código que se imprime en la etiqueta pesada (modal
 *    de peso de caja) y que el cajero escanea con la pistola para cobrar rápido.
 *  - Dos representaciones del mismo contenido:
 *    · CODE128 NUMÉRICO (pistola USB estándar): `99<PLU 5 dígitos><peso 5 dígitos>`
 *      — SOLO dígitos: las térmicas económicas y los escáneres corrompen
 *      caracteres alfanuméricos/símbolos en CODE128 (| → ], - → ', etc.), por
 *      eso el formato es puramente numérico, como el de las básculas comerciales.
 *      El PLU se deriva del `Producto.plu` configurable (código de báscula del
 *      carnicero) y, si no existe, de forma estable del `Producto.codigo`
 *      (mismo hash al imprimir y al escanear → resuelve el producto).
 *    · QR (pistolas con QR o cámara del celular): JSON `{"a":"<codigo>","g":<gramos>,"t":<total>}`
 *      — el QR no corrompe texto, así que conserva el código interno real.
 *  - RESUMEN DE PEDIDO (todo el carrito en UNA etiqueta, impresa desde el
 *    carrito). AMBOS formatos codifican TODAS las líneas del carrito — por
 *    peso (gramos) Y por unidad (cantidad):
 *    · CODE128: `99` + por item `<tipo><PLU 5><valor 5>` (tipo=1 peso en
 *      gramos, tipo=2 cantidad de unidades). Si cabe en el ancho del papel se
 *      imprime UN solo código; si el carrito es grande, splitOrderCode128 lo
 *      divide en PARTES `99<parte 2><total 2><bloques>` que se escanean en
 *      orden y el sistema acumula hasta completar el pedido (funciona sin
 *      lector QR).
 *    · QR: JSON `{"p":[{"a","g"}|{"a","q"},…]}` — un solo escaneo con lector
 *      2D carga TODO el pedido de una vez (sin límite de ancho).
 *  - `t` (total esperado en pesos) es INFORMATIVO: al escanear, el precio se
 *    recalcula SIEMPRE con el `precioPorKilo` del catálogo (fuente única de
 *    verdad, anti-fraude).
 *
 * QUÉ EXPORTA:
 *  - Tipos: WeighLabelDecoded, WeighOrderDecodedItem, WeighOrderItemInput
 *  - weighPluOfProduct(product) → PLU de 5 dígitos (configurado o hash)
 *  - weighPluFromProductCode(code) → PLU numérico estable de 5 dígitos
 *  - encodeWeighLabelCode128(productCode, pesoKg, plu?)
 *  - encodeWeighLabelQr(productCode, pesoKg, precioTotal?)
 *  - encodeOrderCode128(items) — resumen multi-ítem (código completo)
 *  - splitOrderCode128(code, maxDigits) — divide el código en partes
 *    escaneables en orden cuando no cabe en el papel
 *  - encodeOrderQr(items) — resumen multi-ítem (QR JSON)
 *  - decodeWeighLabel(rawCode) → WeighLabelDecoded | null
 *
 * QUÉ NO DEBE HACER:
 *  - No toca UI, storage ni hardware.
 *  - No conoce de productos ni precios (recibe strings/números).
 */
export interface WeighLabelDecoded {
  /** PLU numérico (formato CODE128) — resolver por weighPluOfProduct. */
  productRef?: string;
  /** Código interno del producto (formato QR) — resolver por Producto.codigo. */
  productCode?: string;
  pesoGramos: number;
  precioTotal?: number;
  /** Formato resumen de pedido (multi-ítem) de etiquetas impresas antes. */
  items?: WeighOrderDecodedItem[];
  /** Número de parte escaneada (CODE128 de resumen dividido en partes). */
  parteActual?: number;
  /** Total de partes del resumen (1 = único, no hay que acumular). */
  totalPartes?: number;
  /**
   * Token de venta en espera (etiqueta impresa desde el carrito): el código
   * corto `98<token 8>` / QR `{"w":token}` apunta a la venta en espera que
   * ya guardó el pedido completo — al escanearlo se recupera TODO.
   */
  ventaEsperaToken?: string;
}

/** Item decodificado de un resumen de pedido (etiqueta/QR multi-ítem). */
export interface WeighOrderDecodedItem {
  /** PLU numérico (formato CODE128) — resolver por weighPluOfProduct. */
  productRef?: string;
  /** Código interno del producto (QR lo conserva). */
  productCode?: string;
  /** Peso en gramos cuando el producto se vende por peso. */
  pesoGramos?: number;
  /** Cantidad de unidades cuando el producto se vende por unidad. */
  cantidad?: number;
}

/** Item codificable del QR autocontenido de venta en espera. */
export interface ParkedSaleQrItem {
  /** Código interno del producto (Producto.codigo ?? id). */
  productCode: string;
  /** Peso en gramos cuando la línea es por peso. */
  pesoGramos?: number;
  /** Cantidad de unidades cuando la línea es por unidad. */
  cantidad?: number;
}

/** Marcador fijo del CODE128 numérico (nunca inicia un EAN-13 de producto). */
export const WEIGH_BARCODE_PREFIX = "99";
/** Prefijo del código de venta en espera (etiqueta impresa desde el carrito). */
export const PARKED_SALE_BARCODE_PREFIX = "98";
const PLU_DIGITS = 5;
const GRAMS_DIGITS = 5;
const MAX_GRAMS = 99999;
const ORDER_ITEM_DIGITS = 11;
/** Máximo de líneas de un resumen multi-ítem de etiquetas impresas antes. */
export const MAX_ORDER_ITEMS = 30;
const PARKED_TOKEN_DIGITS = 8;

/**
 * PLU de 5 dígitos de un producto: prioriza el `plu` configurable (código de
 * báscula del carnicero) y cae al hash estable del `codigo` si no existe.
 * Devuelve string de 5 dígitos (ej. plu "7" → "00007").
 */
export function weighPluOfProduct(product: {
  plu?: string | null;
  codigo?: string | null;
}): string {
  const plu = String(product?.plu ?? "")
    .trim()
    .replace(/\D/g, "");
  if (plu) return plu.padStart(PLU_DIGITS, "0").slice(-PLU_DIGITS);
  return weighPluFromProductCode(product?.codigo ?? "");
}

/**
 * PLU numérico estable (5 dígitos) derivado del código interno del producto.
 * El mismo hash se usa al imprimir y al escanear, por lo que resuelve el
 * producto sin necesidad de campos nuevos en el catálogo.
 */
export function weighPluFromProductCode(code: string): string {
  let hash = 0;
  const s = String(code || "")
    .trim()
    .toUpperCase();
  for (let i = 0; i < s.length; i++) {
    hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  }
  return (hash % 100000).toString().padStart(PLU_DIGITS, "0");
}

const toGramos = (pesoKg: number): number => {
  const grams = Math.round(Number(pesoKg) * 1000);
  if (!Number.isFinite(grams) || grams <= 0) return 0;
  return Math.min(MAX_GRAMS, grams);
};

/**
 * Codifica una etiqueta pesada en formato CODE128 NUMÉRICO (solo dígitos).
 * Estructura: `99` + PLU(5) + peso en gramos(5) — ej. "990007802000".
 * Total 12 dígitos → compatible con térmicas económicas y cualquier pistola.
 * El PLU usa el `plu` configurable del producto si se pasa.
 */
export function encodeWeighLabelCode128(
  productCode: string,
  pesoKg: number,
  plu?: string | null,
): string {
  const pluDigits =
    plu != null && String(plu).trim() !== ""
      ? weighPluOfProduct({ plu })
      : weighPluFromProductCode(productCode);
  const grams = toGramos(pesoKg);
  return `${WEIGH_BARCODE_PREFIX}${pluDigits}${grams
    .toString()
    .padStart(GRAMS_DIGITS, "0")}`;
}

/**
 * Codifica una etiqueta pesada en formato QR (JSON compacto).
 * Ejemplo: {"a":"CAR-001","g":2000,"t":44000}
 */
export function encodeWeighLabelQr(
  productCode: string,
  pesoKg: number,
  precioTotal?: number,
): string {
  const code = String(productCode || "")
    .trim()
    .toUpperCase();
  const grams = toGramos(pesoKg);
  const data: Record<string, number | string> = { a: code, g: grams };
  const total = Math.round(Number(precioTotal));
  if (Number.isFinite(total) && total >= 0) data.t = total;
  return JSON.stringify(data);
}

/**
 * Codifica el código de barras de la etiqueta de VENTA EN ESPERA (impresa
 * desde el carrito): `98` + token numérico de 8 dígitos (10 dígitos en total)
 * — un solo código corto que cabe SIEMPRE con barra ancha y legible. Al
 * escanearlo, el sistema recupera la venta en espera completa (el pedido ya
 * está guardado, el código solo apunta a él — nada de cargar items en el
 * código).
 */
export function encodeParkedSaleCode128(token: string): string {
  const digits = String(token || "")
    .trim()
    .replace(/\D/g, "");
  const padded = digits
    .padStart(PARKED_TOKEN_DIGITS, "0")
    .slice(-PARKED_TOKEN_DIGITS);
  return `${PARKED_SALE_BARCODE_PREFIX}${padded}`;
}

/**
 * Codifica el QR de la etiqueta de VENTA EN ESPERA.
 *
 * Dos niveles según `items`:
 *  · Sin items: `{"w":"<token>"}` — referencia a la venta en espera guardada
 *    (mismo dispositivo/backend).
 *  · Con items (AUTOCONTENIDO): `{"w":"<token>","p":[{"a","g"}|{"a","q"},…]}`
 *    — el QR lleva TODO el pedido adentro. Un escaneo en CUALQUIER equipo o
 *    sesión recupera el pedido completo aunque la venta en espera no exista
 *    allí (otro dispositivo, purgada, expirada o sin backend): el código no
 *    caduca ni depende de la venta guardada.
 */
export function encodeParkedSaleQr(
  token: string,
  items?: ParkedSaleQrItem[],
): string {
  const data: Record<string, unknown> = {
    w: String(token || "")
      .trim()
      .replace(/\D/g, ""),
  };
  const clean = (Array.isArray(items) ? items : [])
    .slice(0, MAX_ORDER_ITEMS)
    .filter(
      (it): it is ParkedSaleQrItem =>
        !!it &&
        !!String(it.productCode || "").trim() &&
        ((it.pesoGramos != null && it.pesoGramos > 0) ||
          (it.cantidad != null && it.cantidad > 0)),
    )
    .map((it) => {
      const entry: Record<string, unknown> = {
        a: String(it.productCode).trim().toUpperCase(),
      };
      if (it.pesoGramos != null && it.pesoGramos > 0) {
        entry.g = Math.round(it.pesoGramos);
      } else if (it.cantidad != null && it.cantidad > 0) {
        entry.q = Math.round(it.cantidad);
      }
      return entry;
    });
  if (clean.length > 0) data.p = clean;
  return JSON.stringify(data);
}

/**
 * Parsea un JSON de objeto tolerante a llaves cortadas: si la cámara/lector
 * entregó el fragmento sin `{` inicial (o sin `}` final), lo reconstruye.
 * Devuelve null si no es un objeto JSON válido.
 */
const parseJsonLike = (raw: string): Record<string, unknown> | null => {
  const candidates: string[] = [];
  if (raw.startsWith("{")) {
    candidates.push(raw);
  } else {
    candidates.push(`{${raw}`);
  }
  const first = candidates[0];
  if (!first.endsWith("}")) candidates.push(`${first}}`);
  for (const c of candidates) {
    try {
      const parsed = JSON.parse(c);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      /* siguiente candidato */
    }
  }
  return null;
};

const decodeOrderBlock = (block: string): WeighOrderDecodedItem | null => {
  if (block.length !== ORDER_ITEM_DIGITS) return null;
  const tipo = block[0];
  const productRef = block.slice(1, 1 + PLU_DIGITS);
  const valor = Number(block.slice(1 + PLU_DIGITS));
  if (!Number.isFinite(valor) || valor <= 0) return null;
  if (tipo === "1") return { productRef, pesoGramos: valor };
  if (tipo === "2") return { productRef, cantidad: valor };
  return null;
};

/** Decodifica el array `p` del QR de resumen/autocontenido. null si es inválido. */
const decodeQrOrderItems = (
  entries: unknown[],
): WeighOrderDecodedItem[] | null => {
  if (!Array.isArray(entries) || entries.length === 0) return null;
  const items: WeighOrderDecodedItem[] = [];
  for (const raw of entries.slice(0, MAX_ORDER_ITEMS)) {
    const entry = raw as Record<string, unknown>;
    const productCode = String(entry.a ?? "")
      .trim()
      .toUpperCase();
    if (!productCode) return null;
    const grams = Number(entry.g);
    const qty = Number(entry.q);
    const item: WeighOrderDecodedItem = { productCode };
    if (Number.isFinite(grams) && grams > 0) {
      item.pesoGramos = Math.round(grams);
    } else if (Number.isFinite(qty) && qty > 0) {
      item.cantidad = Math.round(qty);
    } else {
      return null;
    }
    items.push(item);
  }
  return items;
};

/**
 * Intenta decodificar un escaneo como etiqueta ALTENOT.
 * Acepta:
 *  - VENTA EN ESPERA: CODE128 `98` + token (8 dígitos), QR `{"w":"<token>"}`
 *    o QR AUTOCONTENIDO `{"w":"<token>","p":[…]}` (token + pedido completo
 *    adentro: si el token no existe en el equipo, el caller carga los items)
 *  - Etiqueta individual de pesaje (`99` + PLU + gramos, 12 dígitos)
 *  - Resumen multi-ítem de etiquetas impresas antes (CODE128 por partes y QR
 *    `{"p":[…]}`) — compatibilidad: ya no se imprimen
 *  - QR individual (JSON `{"a","g"}`)
 * Devuelve null si el código no es una etiqueta ALTENOT válida.
 */
export function decodeWeighLabel(rawCode: string): WeighLabelDecoded | null {
  let trimmed = String(rawCode || "").trim();
  if (!trimmed) return null;

  // Padding "0" del CODE128 subconjunto C (se imprime con dígitos pares):
  // los códigos ALTENOT numéricos siempre empiezan con "98"/"99", así que un
  // "0" inicial solo puede ser padding. Los QR empiezan con "{" y no se tocan.
  if (!trimmed.startsWith("{")) {
    trimmed = trimmed.startsWith("0") ? trimmed.slice(1) : trimmed;
  }

  // Formato VENTA EN ESPERA: 98 + token de 8 dígitos (10 dígitos en total).
  if (/^98\d{8}$/.test(trimmed)) {
    return {
      ventaEsperaToken: trimmed.slice(2),
      pesoGramos: 0,
    };
  }

  // Formato CODE128 numérico individual: 99 + 5 dígitos PLU + 5 dígitos gramos
  if (/^99\d{10}$/.test(trimmed)) {
    const productRef = trimmed.slice(2, 2 + PLU_DIGITS);
    const pesoGramos = Number(trimmed.slice(2 + PLU_DIGITS));
    if (pesoGramos > 0) return { productRef, pesoGramos };
    return null;
  }

  // Formato CODE128 RESUMEN por partes: 99 + parte(2) + total(2) + N×11 dígitos
  if (/^99\d{4}(?:\d{11})+$/.test(trimmed)) {
    const parteActual = Number(trimmed.slice(2, 4));
    const totalPartes = Number(trimmed.slice(4, 6));
    if (parteActual < 1 || totalPartes < 1 || parteActual > totalPartes) {
      return null;
    }
    const items: WeighOrderDecodedItem[] = [];
    const body = trimmed.slice(6);
    for (let i = 0; i < body.length; i += ORDER_ITEM_DIGITS) {
      const item = decodeOrderBlock(body.slice(i, i + ORDER_ITEM_DIGITS));
      if (!item) return null;
      items.push(item);
    }
    if (items.length === 0) return null;
    return {
      productRef: items[0].productRef,
      pesoGramos: 0,
      items,
      parteActual,
      totalPartes,
    };
  }

  // Formato CODE128 RESUMEN ÚNICO (todo el carrito en un solo código):
  // 99 + N×11 dígitos (tipo + PLU + valor por item), sin prefijo de parte.
  if (/^99(?:\d{11})+$/.test(trimmed)) {
    const items: WeighOrderDecodedItem[] = [];
    const body = trimmed.slice(2);
    for (let i = 0; i < body.length; i += ORDER_ITEM_DIGITS) {
      const item = decodeOrderBlock(body.slice(i, i + ORDER_ITEM_DIGITS));
      if (!item) return null;
      items.push(item);
    }
    if (items.length === 0) return null;
    return { productRef: items[0].productRef, pesoGramos: 0, items };
  }

  // Formato QR: JSON {"a":"<codigo>","g":<gramos>,"t":<total>}, venta en
  // espera {"w":"<token>"}, autocontenido {"w","p":[…]}, o resumen viejo
  // {"p":[…]}. Tolerancia: si la cámara/lector cortó la llave `{` inicial
  // (o el `}` final), el fragmento se reconstruye y se parsea igual.
  if (trimmed.startsWith("{") || /^"[a-zA-Z]"/.test(trimmed)) {
    const parsed = parseJsonLike(trimmed);
    if (!parsed) return null;

    // Token de venta en espera (puede venir SOLO o con el pedido
    // autocontenido en `p` — si el token no existe en este equipo, el
    // caller usa `items` para cargar el pedido completo desde el código).
    const w = String(parsed.w ?? "")
      .trim()
      .replace(/\D/g, "");
    const items = Array.isArray(parsed.p)
      ? decodeQrOrderItems(parsed.p as unknown[])
      : null;
    if (w) {
      return items && items.length > 0
        ? { ventaEsperaToken: w, pesoGramos: 0, items }
        : { ventaEsperaToken: w, pesoGramos: 0 };
    }

    // Resumen de pedido (multi-ítem) — etiquetas impresas antes
    if (items && items.length > 0) {
      return { productRef: "", pesoGramos: 0, items };
    }

    // Etiqueta individual
    const productCode = String(parsed.a ?? "")
      .trim()
      .toUpperCase();
    const grams = Number(parsed.g);
    if (productCode && Number.isFinite(grams) && grams > 0) {
      const t = Number(parsed.t);
      return {
        productCode,
        pesoGramos: Math.round(grams),
        precioTotal: Number.isFinite(t) && t >= 0 ? Math.round(t) : undefined,
      };
    }
  }

  return null;
}
